// Word-level text diff for a pair of leaf-only block children (RES-561 compare).
//
// Flattens each side's leaves into one string plus a run map, word-diffs the strings,
// then rebuilds each side's children by slicing the ORIGINAL leaf runs at segment
// boundaries — so bold/italic/underline/url/color marks survive splits untouched.
// Removed segments gain diffRemoved (left pane), added segments diffAdded (right
// pane); an adjacent removed+added pair is one logical change and shares a hunk id.

import { diffWordsWithSpace } from 'diff';

// Guard for pathological blocks — beyond this the word-diff cost isn't worth it and
// the caller falls back to whole-block comparison.
export const MAX_DIFF_CHARS = 50000;

// Flatten leaf children into {text, runs:[{start, end, marks}]}. Returns null when a
// child is not a plain leaf (inline elements such as parameters) — callers fall back
// to whole-block comparison rather than mis-slicing.
export function flattenLeaves(children) {
	let text = '';
	const runs = [];
	for (const child of children || []) {
		if (!child || typeof child.text !== 'string' || child.type !== undefined)
			return null;
		const { text: t, ...marks } = child;
		runs.push({ start: text.length, end: text.length + t.length, marks });
		text += t;
	}
	return { text, runs };
}

function sliceRuns(flat, from, to, diffProps) {
	const out = [];
	for (const run of flat.runs) {
		const s = Math.max(run.start, from);
		const e = Math.min(run.end, to);
		if (s >= e)
			continue;
		out.push({ ...run.marks, text: flat.text.slice(s, e), ...diffProps });
	}
	return out;
}

function ensureLeaf(children) {
	return children.length ? children : [{ text: '' }];
}

// Returns {leftChildren, rightChildren, changed} or null when this differ doesn't
// apply (inline elements present, or the block exceeds MAX_DIFF_CHARS).
export default function diffText(leftChildren, rightChildren, ctx) {
	const left = flattenLeaves(leftChildren);
	const right = flattenLeaves(rightChildren);
	if (!left || !right)
		return null;
	if (left.text === right.text)
		return { leftChildren, rightChildren, changed: false };
	if (left.text.length + right.text.length > MAX_DIFF_CHARS)
		return null;
	const parts = diffWordsWithSpace(left.text, right.text);
	const newLeft = [];
	const newRight = [];
	let lp = 0;
	let rp = 0;
	for (let k = 0; k < parts.length; k++) {
		const part = parts[k];
		const len = part.value.length;
		if (part.removed) {
			// A removed part directly followed by an added part is one edit — pair
			// them under a single hunk so navigation treats them as one change.
			const paired = parts[k + 1] && parts[k + 1].added;
			const hunkId = ctx.newHunk(paired ? 'changed' : 'removed');
			newLeft.push(...sliceRuns(left, lp, lp + len, { diffRemoved: true, diffHunk: hunkId }));
			lp += len;
			if (paired) {
				const addLen = parts[k + 1].value.length;
				newRight.push(...sliceRuns(right, rp, rp + addLen, { diffAdded: true, diffHunk: hunkId }));
				rp += addLen;
				k++;
			}
		} else if (part.added) {
			const hunkId = ctx.newHunk('added');
			newRight.push(...sliceRuns(right, rp, rp + len, { diffAdded: true, diffHunk: hunkId }));
			rp += len;
		} else {
			newLeft.push(...sliceRuns(left, lp, lp + len));
			newRight.push(...sliceRuns(right, rp, rp + len));
			lp += len;
			rp += len;
		}
	}
	return { leftChildren: ensureLeaf(newLeft), rightChildren: ensureLeaf(newRight), changed: true };
}
